import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, DollarSign, TrendingUp, AlertTriangle, Clock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const AdminAgentsDashboard = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lowFloatAgents, setLowFloatAgents] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch comprehensive statistics
      const statsResponse = await fetch(`${API_URL}/api/admin/agents/statistics`, {
        credentials: 'include'
      });

      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setStats(data);
      }

      // Fetch low float agents
      const lowFloatResponse = await fetch(`${API_URL}/api/admin/agents/low-float`, {
        credentials: 'include'
      });

      if (lowFloatResponse.ok) {
        const data = await lowFloatResponse.json();
        setLowFloatAgents(data.low_float_agents || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Agent Management Dashboard</h1>
            <p className="text-muted-foreground mt-1">Enterprise-level agent monitoring and control</p>
          </div>
          <Button onClick={() => navigate('/admin/agents')} variant="outline">
            View All Agents <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Primary Statistics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_agents || 0}</div>
              <div className="flex gap-2 mt-2 text-xs">
                <Badge className="bg-green-500">Active: {stats?.active_agents || 0}</Badge>
                <Badge className="bg-gray-500">Inactive: {stats?.inactive_agents || 0}</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Agents</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats?.active_agents || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">
                {stats?.total_agents > 0 ? Math.round((stats.active_agents / stats.total_agents) * 100) : 0}% of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suspended Agents</CardTitle>
              <XCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats?.suspended_agents || 0}</div>
              <p className="text-xs text-muted-foreground mt-2">Requires attention</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Low Float Agents</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats?.low_float_agents || 0}</div>
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 h-auto mt-2 text-xs"
                onClick={() => navigate('/admin/agents/low-float')}
              >
                View Details →
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Financial Statistics */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Float in Circulation</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats?.total_float_in_circulation?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground mt-2">Across all agents</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agent Commissions</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">R {stats?.total_agent_commissions?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-muted-foreground mt-2">
                Total earned: R {stats?.total_commissions_earned?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Float Requests</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats?.pending_float_requests || 0}</div>
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 h-auto mt-2 text-xs"
                onClick={() => navigate('/admin/float-requests')}
              >
                Review Requests →
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Today's Activity */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Float Requests Today</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium">Approved</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">
                    {stats?.approved_requests_today || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium">Rejected</span>
                  </div>
                  <span className="text-2xl font-bold text-red-600">
                    {stats?.rejected_requests_today || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/float-requests')}
              >
                <Clock className="mr-2 h-4 w-4" />
                Manage Float Requests
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/float-history')}
              >
                <TrendingUp className="mr-2 h-4 w-4" />
                View Float History
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/agents/low-float')}
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Low Float Alerts
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Low Float Agents Preview */}
        {lowFloatAgents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Low Float Agents (Top 5)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {lowFloatAgents.slice(0, 5).map((agent, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{agent.name}</p>
                      <p className="text-sm text-muted-foreground">{agent.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-orange-600">
                        R {agent.current_float?.toFixed(2)} / R {agent.float_cap?.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {agent.percentage?.toFixed(1)}% remaining
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {lowFloatAgents.length > 5 && (
                <Button 
                  variant="link" 
                  className="w-full mt-3"
                  onClick={() => navigate('/admin/agents/low-float')}
                >
                  View All {lowFloatAgents.length} Low Float Agents →
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};